import * as React from "react";

import PrefersReducedMotion from "./internal/prefers-reduced-motion.tsx";

export type WithPrefersReducedMotionProps = {
    prefersReducedMotion: boolean;
};

export type WithoutPrefersReducedMotion<T> = Omit<
    T,
    keyof WithPrefersReducedMotionProps
>;

/**
 * HOC for accessing a user's motion preference in an SSR-safe manner.
 */
// TODO (FEI-4896, daniellewhyte): update HOC to use usePrefersReducedMotion
const withPrefersReducedMotion = <
    C extends React.ComponentType<
        React.ComponentProps<C> & WithPrefersReducedMotionProps
    > & {
        displayName?: string | undefined;
    },
>(
    WrappedComponent: C,
) => {
    type OuterProps = Omit<
        JSX.LibraryManagedAttributes<C, React.ComponentProps<C>>,
        keyof WithPrefersReducedMotionProps
    >;

    // Try to create a nice displayName for React Dev Tools.
    const displayName =
        WrappedComponent.displayName || WrappedComponent.name || "Component";

    // Creating the inner component. The calculated Props type here is the where the magic happens.
    const ComponentWithLocation = (props: OuterProps): React.ReactElement => {
        return (
            <PrefersReducedMotion>
                {(prefersReducedMotion) => (
                    <WrappedComponent
                        {...(props as any)}
                        prefersReducedMotion={prefersReducedMotion}
                    />
                )}
            </PrefersReducedMotion>
        );
    };

    ComponentWithLocation.displayName = `withPrefersReducedMotion(${displayName})`;

    return ComponentWithLocation;
};

export default withPrefersReducedMotion;
